/**
 * Module dependencies.
 */

import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { Tour } from 'src/types/tour';
import { media } from 'src/styles/media';
import { tours } from 'src/core/config/tours';
import { useCallback, useRef, useState } from 'react';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Button from 'src/components/core/buttons/button';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Map, { Layer as LayerMap, MapRef, SkyLayer, Source } from 'react-map-gl';
import Metatags from 'src/components/core/metatags';
import Svg from 'src/components/core/svg';
import SwiperCore from 'swiper';
import TriggerNavbarTheme, { useInitialNavbarTheme } from 'src/components/navbar/trigger-navbar-theme';
import arrowDown from 'src/assets/svg/arrow-down.svg';
import arrowUp from 'src/assets/svg/arrow-up.svg';
import head from 'lodash/head';
import map from 'lodash/map';
import mapSvg from 'src/assets/svg/map.svg';
import size from 'lodash/size';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * Mapbox config.
 */

const mapBoxConfig = {
  curve: 1.42,
  pitch: 65,
  zoom: 15.4
};

/**
 * Mapbox skyLayer.
 */

const skyLayer: SkyLayer = {
  id: 'sky',
  paint: {
    'sky-atmosphere-sun': [0.0, 0.0],
    'sky-atmosphere-sun-intensity': 15,
    'sky-type': 'atmosphere'
  },
  type: 'sky'
};

/**
 * Mapbox initial view state.
 */

const initialViewState = {
  curve: 1.42,
  latitude: 39.69480613213948,
  longitude: -8.130237626985304,
  zoom: 6
};

/**
 * `MapBoxWrapper` styled component.
 */

const MapBoxWrapper = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;

  &::after {
    background: transparent linear-gradient(180deg, var(--color-black) 0%, var(--color-black) 100%) 0% 0% no-repeat
      padding-box;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    position: absolute;
    right: 0;
    top: 0;
  }

  .mapboxgl-map {
    overflow: hidden;
  }
`;

/**
 * `StyledSvg` styled component.
 */

const StyledSvg = styled(Svg)`
  color: var(--color-white);
  transition: var(--transition-default);
  transition-property: color;
`;

/**
 * `ArrowsNavWrapper` styled component.
 */

const ArrowsNavWrapper = styled(FadeInAnimation)`
  bottom: 2.25rem;
  display: flex;
  position: fixed;
  z-index: 4;

  ${media.min.lg`
    bottom: 1.5rem;
    flex-direction: column;
    left: 1.5rem;
  `}

  ${media.max.lg`
    left: 50%;
    transform: translateX(-50%);
  `}
`;

/**
 * `ButtonArrowStyle` styled component.
 */

const ButtonArrowStyle = styled.button`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background-color: transparent;
  border: 1px solid var(--color-white);
  color: var(--color-white);
  cursor: pointer;
  display: flex;
  height: 4.125rem;
  justify-content: center;
  outline: 0;
  padding: 0;
  transition: var(--transition-default);
  transition-property: background-color;
  width: 4.125rem;

  &:last-child {
    ${media.min.lg`
      border-top: 0;
    `}

    ${media.max.lg`
      border-left: 0;
    `}
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.25);
  }

  &:disabled {
    pointer-events: none;

    ${StyledSvg} {
      color: var(--color-grey-500);
    }
  }
`;

/**
 * `SlideDot` styled component.
 */

const SlideDot = styled.div`
  align-items: center;
  display: flex;
  height: 18px;
  justify-content: center;
  width: 18px;

  &::before {
    background-color: var(--color-white);
    border-radius: 100%;
    content: '';
    height: 10px;
    opacity: 0.42;
    transition: all 0.3s var(--transition-default-animation) 0.3s;
    width: 10px;
  }
`;

/**
 * `SlideLine` styled component.
 */

const SlideLine = styled.div`
  height: 1px;
  position: relative;
  width: 42px;

  &::after {
    background-color: var(--color-yellow);
    bottom: 0;
    content: '';
    height: 1px;
    left: 0;
    position: absolute;
    transform-origin: bottom left;
    transition: all 0.3s ease-in-out;
    width: 0;
    will-change: transform;
  }
`;

/**
 * `SlideText` styled component.
 */

const SlideText = styled.span`
  color: var(--color-white);
  font-family: var(--font-futura-pt);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.188rem;
  line-height: 1.313rem;
  opacity: 0;
  padding-left: 1rem;
  text-transform: uppercase;
  transition: var(--transition-default);
  transition-property: opacity;
`;

/**
 * `SlideWrapper` styled component.
 */

const SlideWrapper = styled.div`
  align-items: center;
  display: flex;
  padding-left: 1.875rem;

  ${media.min.xxl`
    padding-left: 3rem;
  `}
`;

/**
 * `NavWrapper` styled component.
 */

const NavWrapper = styled(FadeInAnimation)`
  -ms-overflow-style: none;
  bottom: 160px;
  display: none;
  left: 0;
  max-width: 250px;
  overflow-y: hidden;
  position: fixed;
  top: 0;
  z-index: 3;

  ${media.min.xl`
    display: block;
  `}

  > div {
    height: 100%;
  }

  .swiper {
    height: calc(100% + 160px);

    .swiper-slide {
      align-items: center;
      cursor: pointer;
      display: flex;

      &.swiper-slide-active {
        ${SlideDot} {
          &::before {
            background-color: var(--color-yellow);
            height: 18px;
            opacity: 1;
            transition: all 0.3s var(--transition-default-animation);
            width: 18px;
          }
        }

        ${SlideLine} {
          &::after {
            transition-delay: 0.2s;
            width: 100%;
          }
        }

        ${SlideText} {
          opacity: 1;
        }
      }

      &:not(.swiper-slide-active) {
        &:hover {
          ${SlideText} {
            opacity: 0.7;
          }
        }
      }
    }
  }
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Text)`
  color: var(--color-yellow);
  margin-bottom: 0;

  ${media.min.lg`
   margin-bottom: 1.25rem;
 `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  color: var(--color-white);
  margin-bottom: 0.5rem;
  text-align: center;

  ${media.max.sm`
    font-size: 2rem;
    line-height: 1;
  `}
`;

/**
 * `PosTitle` styled component.
 */

const PosTitle = styled.p`
  color: var(--color-white);
  font-family: var(--font-futura-pt-bold);
  font-size: clamp(${17 / baseFontSize}rem, ${clampCalc(17, 28)}, ${28 / baseFontSize}rem);
  letter-spacing: clamp(${2.55 / baseFontSize}rem, ${clampCalc(2.55, 4.2)}, ${4.2 / baseFontSize}rem);
  line-height: 1;
  margin-bottom: 3.5rem;
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  transition: var(--transition-default);
  transition-property: opacity, transform;
`;

/**
 * `selectedTourWrapper` styled component.
 */

const SelectedTourWrapper = styled.div`
  align-items: center;
  bottom: 0;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  left: 0;
  padding: 3.25rem 1.25rem 7.5rem;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;

  ${media.min.lg`
    padding: 1.25rem 6.25rem;
  `}

  &.animate-enter {
    opacity: 0;
    transform: translateY(-2.5rem);

    /* stylelint-disable no-descending-specificity */
    /* stylelint-disable rule-empty-line-before */
    ${Title},
    ${PosTitle},
    ${ButtonWrapper} {
      opacity: 0;
      transform: translateY(-2.5rem);
    }
    /* stylelint-enable no-descending-specificity */
    /* stylelint-enable rule-empty-line-before */
  }

  &.animate-exit {
    opacity: 1;
    transform: translateY(0);

    /* stylelint-disable no-descending-specificity */
    /* stylelint-disable rule-empty-line-before */
    ${Title},
    ${PosTitle},
    ${ButtonWrapper} {
      opacity: 1;
      transform: translateY(0);
    }
    /* stylelint-enable no-descending-specificity */
    /* stylelint-enable rule-empty-line-before */
  }

  &.animate-enter-active,
  &.animate-enter-done {
    opacity: 1;
    transform: translateY(0);

    /* stylelint-disable no-descending-specificity */
    /* stylelint-disable rule-empty-line-before */
    ${Title},
    ${PosTitle},
    ${ButtonWrapper} {
      opacity: 1;
      transform: translateY(0);
    }
    /* stylelint-enable no-descending-specificity */
    /* stylelint-enable rule-empty-line-before */
  }

  &.animate-enter-active {
    transition: var(--transition-default);
    transition-delay: 2s;
    transition-property: opacity, transform;

    /* stylelint-disable no-descending-specificity */
    /* stylelint-disable rule-empty-line-before */
    ${Title}, ${PosTitle} {
      transition: var(--transition-default);
      transition-property: opacity, transform;
    }
    /* stylelint-enable no-descending-specificity */
    /* stylelint-enable rule-empty-line-before */

    ${Title} {
      transition-delay: 2.1s;
    }

    ${PosTitle} {
      transition-delay: 2.2s;
    }

    ${ButtonWrapper} {
      transition-delay: 2.3s;
    }
  }

  &.animate-exit-active {
    opacity: 0;
    transform: translateY(-2.5rem);
    transition: var(--transition-default);
    transition-property: opacity, transform;

    /* stylelint-disable no-descending-specificity */
    /* stylelint-disable rule-empty-line-before */
    ${Title},
    ${PosTitle},
    ${ButtonWrapper} {
      opacity: 0;
      transform: translateY(-2.5rem);
      transition: var(--transition-default);
      transition-property: opacity, transform;
    }
    /* stylelint-enable no-descending-specificity */
    /* stylelint-enable rule-empty-line-before */
  }

  ${media.min.xl`
    padding: 1.25rem 10rem;
  `}
`;

/**
 * `StyledMapSvg` styled component.
 */

const StyledMapSvg = styled(Svg)<{ slugTour: string }>`
  circle {
    opacity: 0;
    transition: var(--transition-default);
    transition-property: opacity;

    &[data-city='${props => props.slugTour}'] {
      opacity: 1;
      transition-delay: 3s;
    }
  }
`;

/**
 * `MapWrapper` styled component.
 */

const MapWrapper = styled(FadeInAnimation)`
  bottom: 2.75rem;
  position: absolute;
  right: 3.5rem;
  z-index: 3;
`;

/**
 * `StyledFadeInUpAnimation` styled component.
 */

const StyledFadeInUpAnimation = styled(FadeInUpAnimation)`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
`;

/**
 * `TourPage` page.
 */

const TourPage = () => {
  const { translate } = useTranslate();
  const mapRef = useRef<MapRef | null>(null);
  const scrollRef = useRef(null);
  const firstTour = head(tours);
  const [selectedTour, setTour] = useState<Tour | null>(null);
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const isMobile = useBreakpoint('lg', 'max');
  const total = size(tours);
  const swiperRef = useRef() as any;
  const routeResolver = useLocalizedRoute();

  const mapFlyTo = useCallback((tour: Tour) => {
    mapRef.current?.flyTo({
      bearing: tour.bearing ?? 0,
      center: [tour.coordinates.lng, tour.coordinates.lat],
      essential: true,
      speed: 1,
      ...mapBoxConfig
    });
  }, []);

  const handleChangeTour = useCallback(
    (tour: Tour) => {
      if (tour) {
        mapFlyTo(tour);
        setTour(tour);
      }
    },
    [mapFlyTo, setTour]
  );

  const onMapLoad = useCallback(() => {
    if (firstTour) {
      mapRef.current?.flyTo({
        center: [firstTour.coordinates.lng, firstTour.coordinates.lat],
        essential: true,
        speed: 0.8,
        ...mapBoxConfig
      });

      setTour(firstTour);
    }
  }, [firstTour]);

  const handleGoToPrev = useCallback(() => {
    swiperRef.current?.swiper.slidePrev();
  }, []);

  const handleGoToNext = useCallback(() => {
    swiperRef.current?.swiper.slideNext();
  }, []);

  useInitialNavbarTheme('light');

  return (
    <>
      <Metatags pageTitle={translate('tour:metatags.title')} title={translate('tour:metatags.title')} />

      <TriggerNavbarTheme colorTheme={'light'} />

      {selectedTour && (
        <>
          <NavWrapper
            options={{
              transitionDelay: 2,
              transitionTime: 1
            }}
          >
            <div ref={scrollRef}>
              <Swiper
                allowTouchMove={false}
                breakpoints={{
                  1920: {
                    slidesPerView: 12
                  }
                }}
                centeredSlides
                direction={'vertical'}
                initialSlide={0}
                onInit={(core: SwiperCore) => {
                  swiperRef.current = core.el;
                }}
                onSlideChange={event => {
                  handleChangeTour(tours[event.activeIndex]);
                  setActiveSlide(event.activeIndex);
                }}
                slideToClickedSlide
                slidesPerView={10}
                spaceBetween={50}
                speed={1000}
              >
                {map(tours, (tour, index) => (
                  <SwiperSlide key={index}>
                    <SlideWrapper>
                      <SlideDot />

                      <SlideLine />

                      <SlideText>{translate(`tour:tours.${tour.slug}.title`)}</SlideText>
                    </SlideWrapper>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </NavWrapper>

          <ArrowsNavWrapper
            options={{
              transitionDelay: 2.2,
              transitionTime: 1
            }}
          >
            <ButtonArrowStyle disabled={activeSlide === 0} onClick={handleGoToPrev}>
              <StyledSvg icon={arrowUp} size={'2.375rem'} />
            </ButtonArrowStyle>

            <ButtonArrowStyle disabled={activeSlide === total - 1} onClick={handleGoToNext}>
              <StyledSvg icon={arrowDown} size={'2.375rem'} />
            </ButtonArrowStyle>
          </ArrowsNavWrapper>

          <StyledFadeInUpAnimation
            options={{
              transitionDelay: 2.6,
              transitionTime: 1
            }}
          >
            <SwitchTransition>
              <CSSTransition classNames={'animate'} key={activeSlide} timeout={{ enter: 2650, exit: 350 }}>
                <SelectedTourWrapper>
                  <SubTitle as={'p'} uppercase variant={'header'}>
                    {translate('tour:episode')}
                  </SubTitle>

                  <Title as={'h2'} uppercase variant={'titleS'}>
                    {translate(`tour:tours.${selectedTour.slug}.title`)}
                  </Title>

                  <PosTitle>
                    {translate('tour:with', {
                      value: translate(`tour:tours.${selectedTour.slug}.person`)
                    })}
                  </PosTitle>

                  <ButtonWrapper>
                    <Button
                      href={routeResolver('tourDetails', {
                        slug: selectedTour.slug
                      })}
                      key={selectedTour.slug}
                      variant={'outlined'}
                    >
                      {translate(`common:actions.explore`)}
                    </Button>
                  </ButtonWrapper>
                </SelectedTourWrapper>
              </CSSTransition>
            </SwitchTransition>
          </StyledFadeInUpAnimation>

          {!isMobile && (
            <MapWrapper
              options={{
                transitionDelay: 2.4,
                transitionTime: 1
              }}
            >
              <StyledMapSvg icon={mapSvg} size={'12.625rem'} slugTour={selectedTour.slug} />
            </MapWrapper>
          )}
        </>
      )}

      <MapBoxWrapper>
        <Map
          attributionControl={false}
          initialViewState={initialViewState}
          interactive={false}
          mapStyle={'mapbox://styles/mapbox/satellite-v9'}
          mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
          onLoad={onMapLoad}
          ref={mapRef}
          terrain={{ exaggeration: 1.5, source: 'mapbox-dem' }}
        >
          <Source id={'mapbox-dem'} type={'raster-dem'} url={'mapbox://mapbox.mapbox-terrain-dem-v1'} />

          <LayerMap {...skyLayer} />
        </Map>
      </MapBoxWrapper>
    </>
  );
};

/**
 * Export `TourPage` page.
 */

export default TourPage;
